import { COMMPASS_PAYOUT_PCT, SOL_CHECKLIST, SOL_STAGES, SOL_STAGE_LABEL, TIERS, type TierKey } from '@commpass/shared';
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { SendIcon } from '../components/Icons';
import { StaffTabs } from '../components/StaffTabs';
import { useToast } from '../components/Toast';
import { useCreateSol, useDeleteSol, useSolutions, useUpdateSol } from '../data/solutions';
import { useSubmissions } from '../data/submissions';
import { ApiError, api } from '../lib/api';
import { currencyInput, fmtDate, money, parseMoney } from '../lib/format';
import type { SolRow, SubmissionRow } from '../types';

type Book = 'new_business' | 'commpass';

type Form = {
  id: string | null;
  customerName: string;
  lob: string;
  carrier: string;
  effectiveDate: string;
  quotedPremium: string;
  boundPremium: string;
  commissionPct: string;
  payoutPct: string;
  notes: string;
  stage: string;
};

const EMPTY: Form = {
  id: null, customerName: '', lob: '', carrier: '', effectiveDate: '',
  quotedPremium: '', boundPremium: '', commissionPct: '', payoutPct: '60', notes: '', stage: 'new',
};

const pctInput = (v: string) => v.replace(/[^0-9.]/g, '').slice(0, 6);

// Bound premium × commission % = total; total × payout % = amount owed out.
function commissions(bound: string, pct: string, payout: string) {
  const b = parseMoney(bound);
  const total = (b * (parseFloat(pct) || 0)) / 100;
  const toPaid = (total * (parseFloat(payout) || 0)) / 100;
  return { total, toPaid };
}

function rowToForm(r: SolRow): Form {
  const d = r.data as Record<string, unknown>;
  const s = (k: string) => String(d[k] ?? '');
  return {
    id: r.id,
    customerName: r.customer_name || s('customerName'),
    lob: s('lob'), carrier: s('carrier'), effectiveDate: s('effectiveDate'),
    quotedPremium: s('quotedPremium'), boundPremium: s('boundPremium'),
    commissionPct: s('commissionPct'), payoutPct: s('payoutPct') || '60',
    notes: s('notes'), stage: r.stage || s('stage') || 'new',
  };
}

function ym(d: Record<string, unknown>): string {
  const v = String(d.effectiveDate ?? '');
  const dt = new Date(v);
  if (Number.isNaN(dt.getTime())) return '';
  return `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}`;
}
function ymLabel(v: string): string {
  const [y, m] = v.split('-');
  const dt = new Date(Number(y), Number(m) - 1, 1);
  return dt.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
}

export function SolutionsTracker() {
  const toast = useToast();
  const { data, isLoading, error } = useSolutions();
  const create = useCreateSol();
  const update = useUpdateSol();
  const del = useDeleteSol();

  const [form, setForm] = useState<Form>(EMPTY);
  const [monthFilter, setMonthFilter] = useState('');
  const [book, setBook] = useState<Book>('new_business');

  const rows = useMemo(() => data ?? [], [data]);
  const months = useMemo(() => {
    const set = new Set<string>();
    for (const r of rows) { const v = ym(r.data); if (v) set.add(v); }
    return [...set].sort().reverse();
  }, [rows]);
  const filtered = useMemo(
    () => (monthFilter ? rows.filter((r) => ym(r.data) === monthFilter) : rows),
    [rows, monthFilter],
  );

  const totals = useMemo(() => {
    let quoted = 0, bound = 0, total = 0, toPaid = 0;
    for (const r of filtered) {
      const d = r.data as Record<string, unknown>;
      quoted += parseMoney(d.quotedPremium);
      bound += parseMoney(d.boundPremium);
      const c = commissions(String(d.boundPremium ?? ''), String(d.commissionPct ?? ''), String(d.payoutPct ?? '60'));
      total += c.total; toPaid += c.toPaid;
    }
    return { quoted, bound, total, toPaid };
  }, [filtered]);

  const live = commissions(form.boundPremium, form.commissionPct, form.payoutPct);
  const set = (k: keyof Form, v: string) => setForm((f) => ({ ...f, [k]: v }));

  async function save() {
    if (!form.customerName.trim()) return toast('Enter an insured / client name');
    const fields: Record<string, unknown> = {
      customerName: form.customerName.trim(),
      lob: form.lob.trim(), carrier: form.carrier.trim(), effectiveDate: form.effectiveDate,
      quotedPremium: form.quotedPremium, boundPremium: form.boundPremium,
      commissionPct: form.commissionPct, payoutPct: form.payoutPct || '60',
      notes: form.notes, stage: form.stage,
    };
    try {
      if (form.id) {
        const existing = rows.find((r) => r.id === form.id);
        await update.mutateAsync({
          id: form.id, stage: form.stage, customerName: form.customerName.trim(),
          data: { ...(existing?.data ?? {}), ...fields },
        });
        toast('Saved');
      } else {
        await create.mutateAsync({ customerName: form.customerName.trim(), stage: form.stage, fields });
        toast('Added to tracker');
      }
      setForm(EMPTY);
    } catch (e) {
      toast(e instanceof ApiError ? e.message : 'Could not save');
    }
  }

  async function toggleCheck(row: SolRow, key: string) {
    const checks = { ...((row.data.checklist as Record<string, boolean>) ?? {}) };
    checks[key] = !checks[key];
    await update.mutateAsync({ id: row.id, data: { ...row.data, checklist: checks } });
  }

  async function sync(id: string) {
    try {
      await api.syncSalesforce('solution', id);
      toast('Sent to Salesforce');
    } catch (e) {
      toast(e instanceof ApiError ? e.message : 'Sync failed');
    }
  }

  async function remove(id: string) {
    if (!window.confirm('Remove this account from the tracker? This cannot be undone.')) return;
    await del.mutateAsync(id);
    if (form.id === id) setForm(EMPTY);
    toast('Removed');
  }

  function exportCsv() {
    const cell = (v: string) => (/[",\n]/.test(v) ? '"' + v.replace(/"/g, '""') + '"' : v);
    const header = ['Insured', 'LOB', 'Effective Date', 'Quoted Premium', 'Bound Premium', 'Commission %', 'Total Commission', 'Commission To Be Paid', 'Payout %', 'Carrier', 'Stage', 'Notes'];
    const lines = [header.join(',')];
    for (const r of filtered) {
      const d = r.data as Record<string, unknown>;
      const c = commissions(String(d.boundPremium ?? ''), String(d.commissionPct ?? ''), String(d.payoutPct ?? '60'));
      lines.push([
        r.customer_name || String(d.customerName ?? ''), String(d.lob ?? ''), String(d.effectiveDate ?? ''),
        String(parseMoney(d.quotedPremium)), String(parseMoney(d.boundPremium)), String(d.commissionPct ?? ''),
        c.total.toFixed(2), c.toPaid.toFixed(2), String(d.payoutPct ?? '60'),
        String(d.carrier ?? ''), SOL_STAGE_LABEL[r.stage] ?? r.stage, String(d.notes ?? '').replace(/\n/g, ' '),
      ].map(cell).join(','));
    }
    lines.push(['TOTAL', '', '', String(totals.quoted), String(totals.bound), '', totals.total.toFixed(2), totals.toPaid.toFixed(2), '', '', '', ''].map(cell).join(','));
    const blob = new Blob([lines.join('\n')], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `AAS Solutions${monthFilter ? ' ' + ymLabel(monthFilter) : ''}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }

  const editingRow = form.id ? rows.find((r) => r.id === form.id) ?? null : null;

  return (
    <div className="wrap">
      <StaffTabs />
      <div className="page-head">
        <div>
          <div className="page-title">AAS Solutions</div>
          <div className="page-sub">
            {book === 'new_business'
              ? 'My New Business — direct-book accounts, pipeline & commission tracking.'
              : 'COMM-PASS — commissions auto-pulled from bound portal submissions.'}
          </div>
        </div>
      </div>

      <div className="aas-tabs" role="tablist">
        <button
          type="button"
          className={`aas-tab${book === 'new_business' ? ' on' : ''}`}
          onClick={() => setBook('new_business')}
        >
          My New Business
        </button>
        <button
          type="button"
          className={`aas-tab${book === 'commpass' ? ' on' : ''}`}
          onClick={() => setBook('commpass')}
        >
          COMM-PASS
        </button>
      </div>

      {book === 'commpass' ? (
        <CommpassBook />
      ) : (
      <>
      {error && <div className="empty"><h3>Couldn't load</h3><p>{(error as Error).message}</p></div>}

      {/* Add / edit form */}
      <div className="panel" style={{ marginBottom: 20 }}>
        <div className="panel-head">
          <h3>{form.id ? 'Edit account' : 'Add an account'}</h3>
          {form.id && <button className="reset-link" onClick={() => setForm(EMPTY)}>+ New / clear</button>}
        </div>
        <div className="panel-pad">
          <div className="fgrid">
            <div className="fgroup" style={{ margin: 0 }}>
              <label className="flabel">Insured / client name</label>
              <input className="finput" value={form.customerName} onChange={(e) => set('customerName', e.target.value)} placeholder="Business or client name" />
            </div>
            <div className="fgroup" style={{ margin: 0 }}>
              <label className="flabel">Line(s) of business</label>
              <input className="finput" value={form.lob} onChange={(e) => set('lob', e.target.value)} placeholder="e.g. GL, UM" />
            </div>
          </div>
          <div className="fgrid-3" style={{ marginTop: 12 }}>
            <div className="fgroup" style={{ margin: 0 }}>
              <label className="flabel">Carrier</label>
              <input className="finput" value={form.carrier} onChange={(e) => set('carrier', e.target.value)} />
            </div>
            <div className="fgroup" style={{ margin: 0 }}>
              <label className="flabel">Effective date</label>
              <input type="date" className="finput" value={form.effectiveDate} onChange={(e) => set('effectiveDate', e.target.value)} />
            </div>
            <div className="fgroup" style={{ margin: 0 }}>
              <label className="flabel">Stage</label>
              <select className="finput" value={form.stage} onChange={(e) => set('stage', e.target.value)}>
                {SOL_STAGES.map((s) => <option key={s.k} value={s.k}>{s.label}</option>)}
              </select>
            </div>
          </div>
          <div className="fgrid-3" style={{ marginTop: 12 }}>
            <div className="fgroup" style={{ margin: 0 }}>
              <label className="flabel">Quoted premium</label>
              <input className="finput" value={form.quotedPremium} onChange={(e) => set('quotedPremium', currencyInput(e.target.value))} placeholder="$" />
            </div>
            <div className="fgroup" style={{ margin: 0 }}>
              <label className="flabel">Bound premium</label>
              <input className="finput" value={form.boundPremium} onChange={(e) => set('boundPremium', currencyInput(e.target.value))} placeholder="$" />
            </div>
            <div className="fgroup" style={{ margin: 0 }}>
              <label className="flabel">Commission %</label>
              <input className="finput" value={form.commissionPct} onChange={(e) => set('commissionPct', pctInput(e.target.value))} placeholder="e.g. 15" />
            </div>
          </div>
          <div className="fgrid" style={{ marginTop: 12 }}>
            <div className="fgroup" style={{ margin: 0 }}>
              <label className="flabel">Payout % <span className="opt">(share to be paid)</span></label>
              <input className="finput" value={form.payoutPct} onChange={(e) => set('payoutPct', pctInput(e.target.value))} placeholder="60" />
            </div>
            <div className="fgroup" style={{ margin: 0 }}>
              <label className="flabel">Notes</label>
              <input className="finput" value={form.notes} onChange={(e) => set('notes', e.target.value)} />
            </div>
          </div>

          <div className="banner" style={{ margin: '14px 0 0', display: 'flex', gap: 24, flexWrap: 'wrap' }}>
            <span style={{ fontSize: 13 }}>Total commission <b style={{ color: 'var(--navy)' }}>{money(live.total)}</b></span>
            <span style={{ fontSize: 13 }}>Commission to be paid <b style={{ color: 'var(--orange-deep)' }}>{money(live.toPaid)}</b> <span className="sub-meta" style={{ display: 'inline' }}>at {form.payoutPct || '60'}%</span></span>
          </div>

          <div style={{ display: 'flex', gap: 8, marginTop: 14 }}>
            <button className="btn btn-accent" onClick={() => void save()} disabled={create.isPending || update.isPending}>
              {form.id ? (update.isPending ? 'Saving…' : 'Save changes') : (create.isPending ? 'Adding…' : 'Add to tracker')}
            </button>
            {form.id && <button className="btn btn-ghost" onClick={() => setForm(EMPTY)}>Cancel</button>}
          </div>

          {editingRow && (
            <>
              <hr style={{ border: 'none', borderTop: '1px solid var(--border)', margin: '16px 0' }} />
              <label className="flabel">Checklist</label>
              <div style={{ display: 'grid', gap: 6, margin: '6px 0 14px' }}>
                {SOL_CHECKLIST.map(([key, label]) => (
                  <label key={key} style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 13 }}>
                    <input
                      type="checkbox"
                      checked={!!((editingRow.data.checklist as Record<string, boolean>) ?? {})[key]}
                      onChange={() => void toggleCheck(editingRow, key)}
                    />
                    {label}
                  </label>
                ))}
              </div>
              <button className="btn btn-ghost btn-sm" onClick={() => void sync(editingRow.id)}>
                <SendIcon width={15} height={15} /> Sync to Salesforce
              </button>
            </>
          )}
        </div>
      </div>

      {/* Table */}
      <div className="panel">
        <div className="panel-head">
          <h3>Accounts</h3>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <select className="finput" style={{ width: 'auto', padding: '6px 10px', fontSize: 13 }} value={monthFilter} onChange={(e) => setMonthFilter(e.target.value)}>
              <option value="">All months</option>
              {months.map((m) => <option key={m} value={m}>{ymLabel(m)}</option>)}
            </select>
            <button className="btn btn-ghost btn-sm" onClick={exportCsv} disabled={filtered.length === 0}>Export CSV</button>
          </div>
        </div>
        <div className="panel-pad">
          {isLoading ? (
            <p className="sub-meta">Loading…</p>
          ) : filtered.length === 0 ? (
            <p className="sub-meta">No accounts yet. Add one above to start tracking.</p>
          ) : (
            <div className="sol-table-wrap">
              <table className="sol-table">
                <thead>
                  <tr>
                    <th>Insured</th><th>LOB</th><th>Effective</th>
                    <th className="num">Quoted</th><th className="num">Bound</th><th className="num">Comm %</th>
                    <th className="num">Total Comm</th><th className="num">To Be Paid</th>
                    <th>Carrier</th><th>Stage</th><th></th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((r) => {
                    const d = r.data as Record<string, unknown>;
                    const c = commissions(String(d.boundPremium ?? ''), String(d.commissionPct ?? ''), String(d.payoutPct ?? '60'));
                    return (
                      <tr key={r.id} onClick={() => { setForm(rowToForm(r)); window.scrollTo({ top: 0, behavior: 'smooth' }); }}>
                        <td><b>{r.customer_name || String(d.customerName ?? '') || '—'}</b></td>
                        <td>{String(d.lob ?? '') || '—'}</td>
                        <td>{d.effectiveDate ? fmtDate(d.effectiveDate) : '—'}</td>
                        <td className="num">{d.quotedPremium ? money(d.quotedPremium) : '—'}</td>
                        <td className="num">{d.boundPremium ? money(d.boundPremium) : '—'}</td>
                        <td className="num">{d.commissionPct ? `${d.commissionPct}%` : '—'}</td>
                        <td className="num">{c.total ? money(c.total) : '—'}</td>
                        <td className="num" style={{ color: 'var(--orange-deep)', fontWeight: 600 }}>{c.toPaid ? money(c.toPaid) : '—'}</td>
                        <td>{String(d.carrier ?? '') || '—'}</td>
                        <td><span className="tier-badge" style={{ marginTop: 0 }}>{SOL_STAGE_LABEL[r.stage] ?? r.stage}</span></td>
                        <td><button className="reset-link" onClick={(e) => { e.stopPropagation(); void remove(r.id); }}>Delete</button></td>
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot>
                  <tr>
                    <td colSpan={3}>TOTAL{monthFilter ? ` · ${ymLabel(monthFilter)}` : ''} ({filtered.length})</td>
                    <td className="num">{money(totals.quoted)}</td>
                    <td className="num">{money(totals.bound)}</td>
                    <td></td>
                    <td className="num">{money(totals.total)}</td>
                    <td className="num">{money(totals.toPaid)}</td>
                    <td colSpan={3}></td>
                  </tr>
                </tfoot>
              </table>
            </div>
          )}
        </div>
      </div>
      </>
      )}
    </div>
  );
}

// ---- COMM-PASS commission book (auto-pull) ----
// Read-only: derived from bound `submissions`. Staff enter bound premium + commission %
// (+ effective date) on the submission detail; the producer's take is a FIXED 30% of the
// total commission regardless of tier.

// Bucket a bound submission into a YYYY-MM month: prefer the entered effective date,
// else the timestamp it was marked bound, else the row's created date.
function subMonth(s: SubmissionRow): string {
  const eff = String(s.data.effectiveDate ?? '');
  const m = /^(\d{4})-(\d{2})/.exec(eff);
  if (m) return `${m[1]}-${m[2]}`;
  const hist = Array.isArray(s.data.statusHistory) ? s.data.statusHistory : [];
  const boundAt = [...hist].reverse().find((h) => h.status === 'bound')?.at;
  const dt = new Date(boundAt ?? s.created_at);
  if (Number.isNaN(dt.getTime())) return '';
  return `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}`;
}

function commpassRow(s: SubmissionRow) {
  const boundPremium = parseMoney(s.data.boundPremium);
  const pct = parseFloat(String(s.data.commissionPct ?? '')) || 0;
  const totalComm = (boundPremium * pct) / 100;
  const youMake = (totalComm * COMMPASS_PAYOUT_PCT) / 100;
  return { boundPremium, pct, totalComm, youMake };
}

function tierName(t: string | null): string {
  if (!t) return '—';
  return TIERS[t as TierKey]?.name ?? t;
}

function CommpassBook() {
  const navigate = useNavigate();
  const { data, isLoading, error } = useSubmissions();
  const [monthFilter, setMonthFilter] = useState('');
  // Ids the user has ticked to export. Empty = export whatever the month filter shows.
  const [selected, setSelected] = useState<Set<string>>(new Set());

  const rows = useMemo(() => (data ?? []).filter((s) => s.status === 'bound'), [data]);
  const months = useMemo(() => {
    const set = new Set<string>();
    for (const s of rows) { const v = subMonth(s); if (v) set.add(v); }
    return [...set].sort().reverse();
  }, [rows]);
  const filtered = useMemo(
    () => (monthFilter ? rows.filter((s) => subMonth(s) === monthFilter) : rows),
    [rows, monthFilter],
  );

  const totals = useMemo(() => {
    let bound = 0, totalComm = 0, youMake = 0;
    for (const s of filtered) {
      const c = commpassRow(s);
      bound += c.boundPremium; totalComm += c.totalComm; youMake += c.youMake;
    }
    return { bound, totalComm, youMake };
  }, [filtered]);

  const missing = useMemo(
    () => filtered.filter((s) => !commpassRow(s).boundPremium || !commpassRow(s).pct).length,
    [filtered],
  );

  // Selection, scoped to the rows currently shown. Toggling a row or "select all"
  // only ever touches ids in the current (filtered) view.
  const allFilteredSelected = filtered.length > 0 && filtered.every((s) => selected.has(s.id));
  const someFilteredSelected = filtered.some((s) => selected.has(s.id));
  function toggleRow(id: string) {
    setSelected((cur) => {
      const next = new Set(cur);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }
  function toggleAll() {
    setSelected((cur) => {
      const next = new Set(cur);
      if (allFilteredSelected) filtered.forEach((s) => next.delete(s.id));
      else filtered.forEach((s) => next.add(s.id));
      return next;
    });
  }

  function exportCsv() {
    // Export the ticked rows if any are ticked; otherwise the whole filtered view.
    const list = selected.size ? rows.filter((s) => selected.has(s.id)) : filtered;
    if (!list.length) return;
    const cell = (v: string) => (/[",\n]/.test(v) ? '"' + v.replace(/"/g, '""') + '"' : v);
    const header = ['Insured', 'Agency', 'Tier', 'Effective Date', 'Bound Premium', 'Commission %', 'Total Commission', `You Make (${COMMPASS_PAYOUT_PCT}%)`];
    const lines = [header.join(',')];
    let tBound = 0, tComm = 0, tYou = 0;
    for (const s of list) {
      const c = commpassRow(s);
      tBound += c.boundPremium; tComm += c.totalComm; tYou += c.youMake;
      lines.push([
        s.business_name || String(s.data.businessName ?? ''),
        String(s.data.agencyName ?? ''),
        tierName(s.tier),
        String(s.data.effectiveDate ?? ''),
        String(c.boundPremium),
        s.data.commissionPct ? String(s.data.commissionPct) : '',
        c.totalComm.toFixed(2),
        c.youMake.toFixed(2),
      ].map(cell).join(','));
    }
    lines.push(['TOTAL', '', '', '', String(tBound), '', tComm.toFixed(2), tYou.toFixed(2)].map(cell).join(','));
    const blob = new Blob([lines.join('\n')], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    const suffix = selected.size ? ' (selected)' : monthFilter ? ' ' + ymLabel(monthFilter) : '';
    a.download = `COMM-PASS Commissions${suffix}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }

  return (
    <>
      {error && <div className="empty"><h3>Couldn't load</h3><p>{(error as Error).message}</p></div>}

      {/* Totals bar */}
      <div className="panel" style={{ marginBottom: 20 }}>
        <div className="banner" style={{ margin: 0, display: 'flex', gap: 28, flexWrap: 'wrap' }}>
          <span style={{ fontSize: 13 }}>Bound premium <b style={{ color: 'var(--navy)' }}>{money(totals.bound)}</b></span>
          <span style={{ fontSize: 13 }}>Total commission <b style={{ color: 'var(--navy)' }}>{money(totals.totalComm)}</b></span>
          <span style={{ fontSize: 13 }}>You make <b style={{ color: 'var(--orange-deep)' }}>{money(totals.youMake)}</b> <span className="sub-meta" style={{ display: 'inline' }}>at {COMMPASS_PAYOUT_PCT}% of commission</span></span>
        </div>
      </div>

      <div className="panel">
        <div className="panel-head">
          <h3>Bound deals</h3>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            {selected.size > 0 && (
              <>
                <span className="sub-meta">{selected.size} selected</span>
                <button className="reset-link" onClick={() => setSelected(new Set())}>Clear</button>
              </>
            )}
            <select className="finput" style={{ width: 'auto', padding: '6px 10px', fontSize: 13 }} value={monthFilter} onChange={(e) => setMonthFilter(e.target.value)}>
              <option value="">All months</option>
              {months.map((m) => <option key={m} value={m}>{ymLabel(m)}</option>)}
            </select>
            <button className="btn btn-ghost btn-sm" onClick={exportCsv} disabled={filtered.length === 0 && selected.size === 0}>
              {selected.size > 0 ? `Export ${selected.size} selected` : 'Export CSV'}
            </button>
          </div>
        </div>
        <div className="panel-pad">
          {isLoading ? (
            <p className="sub-meta">Loading…</p>
          ) : filtered.length === 0 ? (
            <p className="sub-meta">No bound deals yet. When a submission is marked Bound and its commission details are entered, it appears here automatically.</p>
          ) : (
            <>
              {missing > 0 && (
                <p className="sub-meta" style={{ marginTop: 0, marginBottom: 12 }}>
                  {missing} bound {missing === 1 ? 'deal is' : 'deals are'} missing a bound premium or commission % — open the submission to add them.
                </p>
              )}
              <div className="sol-table-wrap">
                <table className="sol-table">
                  <thead>
                    <tr>
                      <th style={{ width: 28 }}>
                        <input
                          type="checkbox"
                          checked={allFilteredSelected}
                          ref={(el) => {
                            if (el) el.indeterminate = !allFilteredSelected && someFilteredSelected;
                          }}
                          onChange={toggleAll}
                          aria-label="Select all"
                        />
                      </th>
                      <th>Insured</th><th>Agency</th><th>Tier</th><th>Effective</th>
                      <th className="num">Bound Premium</th><th className="num">Comm %</th>
                      <th className="num">Total Comm</th><th className="num">You Make</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.map((s) => {
                      const c = commpassRow(s);
                      return (
                        <tr key={s.id} onClick={() => navigate(`/s/${s.id}`)} title="Open submission">
                          <td onClick={(e) => e.stopPropagation()}>
                            <input
                              type="checkbox"
                              checked={selected.has(s.id)}
                              onChange={() => toggleRow(s.id)}
                              aria-label={`Select ${s.business_name || s.id}`}
                            />
                          </td>
                          <td><b>{s.business_name || String(s.data.businessName ?? '') || '—'}</b></td>
                          <td>{String(s.data.agencyName ?? '') || '—'}</td>
                          <td>{tierName(s.tier)}</td>
                          <td>{s.data.effectiveDate ? fmtDate(s.data.effectiveDate) : '—'}</td>
                          <td className="num">{c.boundPremium ? money(c.boundPremium) : '—'}</td>
                          <td className="num">{c.pct ? `${c.pct}%` : '—'}</td>
                          <td className="num">{c.totalComm ? money(c.totalComm) : '—'}</td>
                          <td className="num" style={{ color: 'var(--orange-deep)', fontWeight: 600 }}>{c.youMake ? money(c.youMake) : '—'}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                  <tfoot>
                    <tr>
                      <td colSpan={5}>TOTAL{monthFilter ? ` · ${ymLabel(monthFilter)}` : ''} ({filtered.length})</td>
                      <td className="num">{money(totals.bound)}</td>
                      <td></td>
                      <td className="num">{money(totals.totalComm)}</td>
                      <td className="num">{money(totals.youMake)}</td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </>
          )}
        </div>
      </div>
    </>
  );
}
